/**
 * Reading a client's ticker file for the Tickers box.
 *
 * The old upload dropped row 1 and split every comma, so a book exported with
 * Ticker, Name and Weight columns came in as a jumble of symbols, company
 * names and numbers — and a file with no header lost its first ticker. This
 * reads the file as a table instead: quoted cells, commas, semicolons or tabs;
 * a header spotted by its names or by its shape; the ticker column picked by
 * name or by what its values look like. The check step (CsvImportStep) shows
 * all of that before anything is added, so a wrong guess is one click away
 * from right.
 *
 * Its own module, importing nothing, so the rules can be tested in node.
 */

/** A cell a ticker column would hold: AAPL, BRK.B, VOD.L, 7203.T, EURUSD. */
const SYMBOL_CELL =
  /^\$?(?:[A-Z][A-Z0-9]{0,6}(?:[./-][A-Z0-9]{1,4})?|[0-9]{3,6}(?:\.[A-Z]{1,3})?)$/;

/** Header names that say "the symbols are here". */
const TICKER_HEADER = /^(?:tickers?|symbols?|ric|rics|codes?|ticker symbol|bbg ticker|bloomberg ticker|stock|instrument|security id)$/i;

/** Header names a ticker file commonly carries alongside. */
const OTHER_HEADER = /^(?:name|names|company|company name|description|security|security name|weight|weight %|weights?|%|shares|quantity|qty|price|value|market value|sector|country|currency|isin|sedol|cusip|notes?)$/i;

/**
 * Whether a cell reads as a symbol. Read in capitals, so a file of "aapl"
 * still counts, but a capitalised word is a name: "Apple", not APPLE.
 */
export function looksLikeSymbol(cell: string): boolean {
  const value = cell.trim();
  if (/^[A-Z][a-z]+$/.test(value)) return false;
  return SYMBOL_CELL.test(value.toUpperCase());
}

/** The delimiter the first line uses most, outside quotes. */
function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  let best = ",";
  let bestCount = 0;
  for (const delimiter of [",", ";", "\t"]) {
    let count = 0;
    let quoted = false;
    for (const ch of firstLine) {
      if (ch === '"') quoted = !quoted;
      else if (!quoted && ch === delimiter) count++;
    }
    if (count > bestCount) {
      best = delimiter;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Parses CSV text into trimmed rows, dropping empty ones. Quoted cells may
 * hold the delimiter, doubled quotes and line breaks.
 */
export function parseCsv(text: string): string[][] {
  const source = text.replace(/^\uFEFF/, "");
  const delimiter = detectDelimiter(source);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;

  const endCell = () => {
    row.push(cell.trim());
    cell = "";
  };
  const endRow = () => {
    endCell();
    if (row.some((c) => c.length > 0)) rows.push(row);
    row = [];
  };

  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    if (quoted) {
      if (ch === '"' && source[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === delimiter) endCell();
    else if (ch === "\n") endRow();
    else if (ch !== "\r") cell += ch;
  }
  endRow();
  return rows;
}

/**
 * Whether the first row is a header. Names count first — a "Ticker" or
 * "Name" cell — then shape: a first row with no symbol-shaped cell above
 * rows that have one is a header whatever it says.
 */
export function detectHeader(rows: string[][]): boolean {
  if (rows.length === 0) return false;
  const [first, ...rest] = rows;
  if (first.some((c) => TICKER_HEADER.test(c) || OTHER_HEADER.test(c))) return true;
  if (rest.length === 0) return false;
  const width = Math.max(...rows.map((r) => r.length));
  for (let col = 0; col < width; col++) {
    const below = rest.slice(0, 20).map((r) => r[col] ?? "").filter(Boolean);
    const symbolsBelow = below.filter(looksLikeSymbol).length;
    if (symbolsBelow >= Math.max(1, below.length * 0.8) && !looksLikeSymbol(first[col] ?? "")) {
      return true;
    }
  }
  return false;
}

/** The column the tickers are in: by header name, then by what it holds. */
export function pickTickerColumn(rows: string[][], hasHeader: boolean): number {
  if (rows.length === 0) return 0;
  if (hasHeader) {
    const named = rows[0].findIndex((c) => TICKER_HEADER.test(c));
    if (named !== -1) return named;
  }
  const body = hasHeader ? rows.slice(1) : rows;
  const width = Math.max(...rows.map((r) => r.length));
  let best = 0;
  let bestScore = -1;
  for (let col = 0; col < width; col++) {
    const filled = body.map((r) => r[col] ?? "").filter((c) => c.length > 0);
    // Symbols with letters count double: a column of share counts is all
    // symbol-shaped too (HK and Tokyo codes are numbers), and must not win
    // over the tickers beside it.
    const symbols = filled.filter(looksLikeSymbol);
    const lettered = symbols.filter((c) => /[A-Za-z]/.test(c));
    const score =
      filled.length === 0 ? 0 : (2 * lettered.length + symbols.length) / filled.length;
    if (score > bestScore) {
      best = col;
      bestScore = score;
    }
  }
  return best;
}

/** How much of a column is symbol-shaped, 0–1, ignoring empty cells. */
export function symbolShare(cells: string[]): number {
  const filled = cells.filter((c) => c.trim().length > 0);
  if (filled.length === 0) return 0;
  return filled.filter(looksLikeSymbol).length / filled.length;
}

/** A column that reads as tickers: nearly every filled cell is a symbol. */
export function columnLooksLikeTickers(cells: string[]): boolean {
  return symbolShare(cells) >= 0.8;
}

/** Each column's name for the picker: its header, or "Column 2". */
export function columnLabels(rows: string[][], hasHeader: boolean): string[] {
  const width = rows.length === 0 ? 0 : Math.max(...rows.map((r) => r.length));
  return Array.from({ length: width }, (_, col) => {
    const header = hasHeader ? rows[0]?.[col]?.trim() : "";
    return header || `Column ${col + 1}`;
  });
}

/**
 * The tickers a column adds: upper-cased, a leading "$" dropped, blanks and
 * repeats skipped, file order kept.
 */
export function columnTickers(
  rows: string[][],
  column: number,
  hasHeader: boolean
): string[] {
  const body = hasHeader ? rows.slice(1) : rows;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of body) {
    const symbol = (row[column] ?? "").trim().replace(/^\$/, "").toUpperCase();
    if (!symbol || symbol.length > 30 || seen.has(symbol)) continue;
    seen.add(symbol);
    out.push(symbol);
  }
  return out;
}

/** The first read of a file: header and ticker column guessed. */
export interface CsvRead {
  rows: string[][];
  hasHeader: boolean;
  column: number;
}

export function readTickerCsv(text: string): CsvRead {
  const rows = parseCsv(text);
  const hasHeader = detectHeader(rows);
  return { rows, hasHeader, column: pickTickerColumn(rows, hasHeader) };
}
